import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { Box, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material'
import { DeepcellPrimaryButton } from 'components/shared'

interface MorphotypeMigrationDialogProps {
  open: boolean
  migrationStep: 'idle' | 'morphotypes' | 'differential-features' | 'complete'
  migratedMorphotypesCount?: number
  onClose: () => void
}

/**
 * Dialog that explains morphotype migration to users.
 * Shows clear information about what's being upgraded, why, and what users need to do.
 */
export default function MorphotypeMigrationDialog({
  open,
  migrationStep,
  migratedMorphotypesCount = 0,
  onClose,
}: MorphotypeMigrationDialogProps): JSX.Element {
  const isProcessing = migrationStep !== 'complete'
  const hasDifferentialFeatures = migratedMorphotypesCount > 0

  // Determine status for each step
  const morphotypesStepComplete = migrationStep === 'differential-features' || migrationStep === 'complete'
  const differentialFeaturesStepComplete = migrationStep === 'complete'

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={isProcessing}
      onClose={isProcessing ? undefined : onClose}
    >
      <DialogTitle>Updating Morphotype Format</DialogTitle>

      <DialogContent>
        <Typography variant="body1" paragraph>
          We&apos;re updating your session to use the latest morphotype format. This will only take a moment.
        </Typography>

        <Typography variant="body2" color="text.secondary" paragraph>
          A morphotype is defined by a geometric region in UMAP space.
        </Typography>

        <Typography variant="body2" color="text.secondary" paragraph>
          <strong>Previous Format (Local):</strong> Morphotypes were restricted to the specific plot view and limited to currently filtered cells in your selection.
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          <strong>New Format (Global):</strong> Morphotypes now capture all cells within the geometric region and sync automatically across all multi-plot views that share the same UMAP space.
        </Typography>

        <Typography variant="body2" color="text.secondary" paragraph>
        The benefit is that morphotypes work consistently across different plot views, filter states, and classifier training, which makes comparisons and downstream analysis more reliable.
        </Typography>

        <Typography variant="body2" color="text.secondary" paragraph>
          <strong>What&apos;s happening:</strong>
        </Typography>

        {/* Step 1: Converting morphotypes */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          {morphotypesStepComplete ? (
            <CheckCircleIcon color="success" />
          ) : (
            <CircularProgress size={24} />
          )}
          <Typography variant="body2" color="text.secondary">
            Converting morphotype selections to global format...
          </Typography>
        </Box>

        {/* Step 2: Recalculating differential features (if applicable) */}
        {hasDifferentialFeatures && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            {differentialFeaturesStepComplete ? (
              <CheckCircleIcon color="success" />
            ) : (
              <CircularProgress size={24} />
            )}
            <Typography variant="body2" color="text.secondary">
              Recalculating differential features for {migratedMorphotypesCount} morphotype
              {migratedMorphotypesCount === 1 ? '' : 's'}
            </Typography>
          </Box>
        )}

        {migrationStep === 'complete' && (
          <Box sx={{ mt: 3, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
            <Typography variant="body2">
            Your session has been updated.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center' }}>
        <DeepcellPrimaryButton onClick={onClose} contained disabled={isProcessing}>
          Continue
        </DeepcellPrimaryButton>
      </DialogActions>
    </Dialog>
  )
}
